import { Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"

import { useEditor } from "@craftjs/core"
import { ChevronDown, Circle } from "lucide-react"
import { BreadcrumbItem, BreadcrumbList, BreadcrumbWrapper, cn, DefaultTooltip, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "twyne-new.ui"

import { usePalette } from "@/modules/cms/views/post/components/builder/context"

const MEASURE_ITEM_SELECTOR = '[data-measure-item]'
const MEASURE_SEPARATOR_SELECTOR = '[data-measure-separator]'
const MEASURE_ELLIPSIS_SELECTOR = '[data-measure-ellipsis]'
const DUMMY_HIDDEN_ITEMS = [{ id: '__measure__', label: 'measure' }]

export default function BreadcrumbsBlocks() {
  const containerRef = useRef(null)
  const [actionsWidth, setActionsWidth] = useState(0)
  const { selectedNodeId, nodes } = useEditor(({ nodes, events }) => ({
    selectedNodeId: getFirstSelectedNodeId(events.selected),
    nodes,
  }))

  const breadcrumbPath = useMemo(() => {
    if (!selectedNodeId || !nodes[selectedNodeId]) {
      return []
    }

    const ids = []
    let currentId = selectedNodeId

    while (currentId && nodes[currentId]) {
      ids.push(currentId)
      currentId = nodes[currentId]?.data?.parent
    }

    return ids.reverse().map(id => {
      const node = nodes[id]
      const label = node?.data?.custom?.name || node?.data?.name || id
      return { id, label: label === 'template_root' ? 'root' : label }
    })
  }, [selectedNodeId, nodes])

  const { measureRef, visiblePath, hiddenPath } = useResponsiveBreadcrumbs(breadcrumbPath, containerRef, actionsWidth)

  useLayoutEffect(() => {
    const actions = containerRef.current?.nextElementSibling

    if (!actions) {
      return
    }

    setActionsWidth(actions.offsetWidth)
  }, [breadcrumbPath])

  if (!breadcrumbPath.length) {
    return <div />
  }

  return <>
    <div
      ref={containerRef}
      className='absolute left-2 top-1/4 inset-y-0 flex grow-0 items-center overflow-hidden'
      style={{ right: actionsWidth ? actionsWidth + 16 : 0 }}
    >
      <BreadcrumbWrapper className='relative block w-full min-w-0 max-w-full grow-0 overflow-hidden'>
        <BreadcrumbList className='w-full min-w-0 max-w-full flex-nowrap overflow-hidden whitespace-nowrap'>
          {hiddenPath.length > 0 && <>
            <HiddenBreadcrumbItems items={hiddenPath} />
            <PathSeparator />
          </>}
          {visiblePath.map((item, index) => {
            const isLast = index === visiblePath.length - 1

            return <Fragment key={item.id}>
              <span className='shrink-0 whitespace-nowrap'>
                <Item node={item} nodes={nodes} isCurrent={isLast} />
              </span>
              {!isLast && <PathSeparator />}
            </Fragment>
          })}
        </BreadcrumbList>

        <div ref={measureRef} aria-hidden='true' className='invisible absolute left-0 top-0 flex items-center gap-1.5 whitespace-nowrap pointer-events-none'>
          {breadcrumbPath.map((item, index) => {
            const isLast = index === breadcrumbPath.length - 1

            return <Fragment key={item.id}>
              <span className='shrink-0 whitespace-nowrap'>
                <Item node={item} nodes={nodes} isCurrent={isLast} />
              </span>
              {!isLast && <PathSeparator />}
            </Fragment>
          })}
          <PathSeparator />
          <HiddenBreadcrumbItems items={DUMMY_HIDDEN_ITEMS} />
        </div>
      </BreadcrumbWrapper>
    </div>
  </>
}

function useResponsiveBreadcrumbs(path, containerRef, actionsWidth) {
  const measureRef = useRef(null)
  const [visibleCount, setVisibleCount] = useState(path.length)

  const recalculate = useCallback(() => {
    const container = containerRef?.current
    const measure = measureRef.current

    if (!container || !measure) {
      return
    }

    const itemWidths = Array.from(measure.querySelectorAll(MEASURE_ITEM_SELECTOR)).map(el => el.getBoundingClientRect().width)

    if (!itemWidths.length) {
      return
    }

    const availableWidth = container.clientWidth
    const separatorWidth = measure.querySelector(MEASURE_SEPARATOR_SELECTOR)?.getBoundingClientRect().width || 0
    const ellipsisWidth = measure.querySelector(MEASURE_ELLIPSIS_SELECTOR)?.getBoundingClientRect().width || 0
    const gapWidth = Number.parseFloat(window.getComputedStyle(measure).columnGap) || 0

    for (let count = itemWidths.length; count > 0; count--) {
      const hidden = count < itemWidths.length
      const visibleWidths = itemWidths.slice(itemWidths.length - count)
      const visibleItemsWidth = visibleWidths.reduce((total, width) => total + width, 0)
      const visibleSeparators = Math.max(count - 1, 0)
      const hiddenSeparators = hidden ? 1 : 0
      const hiddenChildren = hidden ? 2 : 0
      const visibleChildren = count + visibleSeparators
      const childrenCount = hiddenChildren + visibleChildren
      const renderedWidth = visibleItemsWidth
        + (visibleSeparators + hiddenSeparators) * separatorWidth
        + (hidden ? ellipsisWidth : 0)
        + Math.max(childrenCount - 1, 0) * gapWidth

      if (renderedWidth <= availableWidth) {
        setVisibleCount(count)
        return
      }
    }

    setVisibleCount(1)
  }, [containerRef])

  useLayoutEffect(() => {
    recalculate()
  }, [path, actionsWidth, recalculate])

  const hiddenCount = Math.max(path.length - visibleCount, 0)

  return {
    measureRef,
    visiblePath: hiddenCount > 0 ? path.slice(hiddenCount) : path,
    hiddenPath: hiddenCount > 0 ? path.slice(0, hiddenCount) : [],
  }
}

function PathSeparator() {
  return <span aria-hidden='true' data-measure-separator className='shrink-0 whitespace-nowrap text-gray-400 dark:text-gray-900'>/</span>
}

function HiddenBreadcrumbItems({ items }) {
  return <DefaultTooltip content={items.map(item => item.label).join(' / ')} triggerProps={{ className: 'shrink-0' }}>
    <span data-measure-ellipsis className='twy-text-base select-none whitespace-nowrap'>...</span>
  </DefaultTooltip>
}

function Item({ node: { id, label }, nodes, isCurrent }) {
  const siblings = useBlockSiblings({ nodeId: id, nodes })
  const [popover, setPopover] = useState(false)
  const { actions: { selectNode } } = useEditor()

  return <>
    <div
      data-measure-item
      className={cn(
        'flex items-center gap-1 shrink-0 whitespace-nowrap',
        id === 'ROOT' ? 'twy-text-base-neutral' : 'twy-text-base',
        popover && 'twy-text-accent!')}>
      <BreadcrumbItem
        className={cn('whitespace-nowrap', !isCurrent && 'cursor-pointer! hover:twy-text-base-accent')}
        onClick={isCurrent ? undefined : () => selectNode(id)}
      >
        {label}
      </BreadcrumbItem>

      {siblings.length ? <BreadcrumbPopover
        siblings={siblings}
        open={popover}
        onOpenChange={setPopover}
      >
        <ChevronDown size={14} className='cursor-pointer! shrink-0 hover:twy-text-base-accent' />
      </BreadcrumbPopover> : null}
    </div>
  </>
}

function BreadcrumbPopover({ children, siblings, open, onOpenChange }) {
  const { actions: { selectNode } } = useEditor()

  if (!siblings.length) {
    return children
  }

  return <>
    <DropdownMenu size="sm" open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger>{children}</DropdownMenuTrigger>
      <DropdownMenuContent>
        {siblings.map(sibling => <DropdownMenuItem key={sibling.id} onClick={() => selectNode(sibling.id)}>
          <span className='flex items-center gap-2'>
            {sibling.icon || <Circle size='16' />}
            {sibling.label}
          </span>
        </DropdownMenuItem>,
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  </>
}

function getFirstSelectedNodeId(selectedNodes) {
  if (!selectedNodes || typeof selectedNodes[Symbol.iterator] !== 'function') {
    return undefined
  }

  const iterator = selectedNodes[Symbol.iterator]()
  return iterator.next().value
}

const useBlockSiblings = ({ nodeId, nodes }) => {
  const palette = usePalette()
  const parentId = nodes[nodeId]?.data?.parent

  if (!parentId || !nodes[parentId]?.data?.nodes?.length) {
    return []
  }

  return nodes[parentId]?.data?.nodes
    .filter(id => id !== nodeId)
    .map(id => {
      const node = nodes[id]
      const label = node?.data?.custom?.name || node?.data?.name || id
      return {
        id,
        label: label === 'template_root' ? 'root' : label,
        icon: palette[node?.data?.name]?.palette?.icon,
      }
    })
}
